export const ROLES = {
  CUSTOMER: 'customer',
  BODYGUARD: 'bodyguard',
  ADMIN: 'admin'
};

export const BODYGUARD_TIERS = {
  1: { name: 'D License', rate: 60, color: 'green' },
  2: { name: 'G License', rate: 80, color: 'yellow' },
  3: { name: 'Tactical', rate: 100, color: 'red' }
};

export const TIER_DESCRIPTIONS = {
  1: 'Professional security personnel trained in basic protection, crowd control, and de-escalation techniques.',
  2: 'Licensed armed security professionals with advanced training in threat assessment and defensive tactics.',
  3: 'Elite protection specialists with military/law enforcement background and tactical operations expertise.'
};

export const SESSION_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  ACTIVE: 'active',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

export const APPLICATION_STATUS = {
  PENDING: 'pending',
  UNDER_REVIEW: 'under_review',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  ADDITIONAL_INFO_REQUIRED: 'additional_info_required'
};

export const PAYMENT_METHODS = {
  CREDIT_CARD: 'credit_card',
  DEBIT_CARD: 'debit_card',
  BANK_ACCOUNT: 'bank_account',
  CASH: 'cash'
};

export const SPECIALIZATIONS = [
  'Personal Protection',
  'Event Security',
  'Executive Protection',
  'Tactical Operations',
  'Surveillance',
  'Risk Assessment'
];

// `value` is what gets submitted and stored; `label` is the English fallback.
// The apply form renders options via t('options.spokenLanguages.<value>') so the
// applicant reads their own language while the record stays canonical.
export const LANGUAGES = [
  { value: 'english', label: 'English' },
  { value: 'spanish', label: 'Spanish' },
  { value: 'french', label: 'French' },
  { value: 'german', label: 'German' },
  { value: 'chinese', label: 'Chinese' },
  { value: 'arabic', label: 'Arabic' },
  { value: 'russian', label: 'Russian' },
  { value: 'japanese', label: 'Japanese' },
  { value: 'korean', label: 'Korean' },
  { value: 'portuguese', label: 'Portuguese' }
];

// Curated security-industry license types; "Other" is appended in the UI
// with a free-text field so uncommon licenses are never blocked.
//
// `value` is the canonical English string that is submitted, stored in
// DynamoDB, matched by validateLicenseEntries (which tests for the
// "Military/Law Enforcement" prefix) and read by the admin console's
// verification panel. It must NEVER be translated — a Spanish applicant and an
// English one produce byte-identical records. `key` selects the display label
// from options.licenseTypes.* in the locale files.
export const LICENSE_TYPES = [
  { key: 'securityGuard', value: 'Security Guard License / Guard Card' },
  { key: 'armedSecurityGuard', value: 'Armed Security Guard License' },
  { key: 'firearmsPermit', value: 'Firearms Permit / CCW' },
  { key: 'privatePatrolOperator', value: 'Private Patrol Operator License' },
  { key: 'privateInvestigator', value: 'Private Investigator License' },
  { key: 'executiveProtection', value: 'Executive Protection / Bodyguard Certification' },
  { key: 'militaryLeActive', value: 'Military/Law Enforcement (Active)' },
  { key: 'militaryLeRetired', value: 'Military/Law Enforcement (Retired)' }
];

// Reverse lookup for rendering a stored license type back into the applicant's
// language (the review step and any future read-back path store the canonical
// value, not the key).
export const LICENSE_TYPE_KEY_BY_VALUE = Object.fromEntries(
  LICENSE_TYPES.map((l) => [l.value, l.key])
);

export const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

export const PROFICIENCY_LEVELS = [
  { value: 'basic', label: 'Basic' },
  { value: 'conversational', label: 'Conversational' },
  { value: 'fluent', label: 'Fluent' },
  { value: 'native', label: 'Native' }
];

export const MARTIAL_ARTS = [
  'Boxing',
  'Karate',
  'Taekwondo',
  'Judo',
  'Brazilian Jiu-Jitsu',
  'Muay Thai',
  'Krav Maga',
  'Mixed Martial Arts (MMA)',
  'Wing Chun',
  'Aikido'
];

export const WEAPON_TYPES = [
  'Handgun',
  'Rifle',
  'Shotgun',
  'Tactical Equipment',
  'Non-lethal Weapons'
];

export const INCIDENT_TYPES = [
  { value: 'safety', label: 'Safety Concern' },
  { value: 'behavioral', label: 'Behavioral Issue' },
  { value: 'technical', label: 'Technical Problem' },
  { value: 'other', label: 'Other' }
];

export const SEVERITY_LEVELS = [
  { value: 'low', label: 'Low', color: 'green' },
  { value: 'medium', label: 'Medium', color: 'yellow' },
  { value: 'high', label: 'High', color: 'orange' },
  { value: 'critical', label: 'Critical', color: 'red' }
];

export const DATE_FORMAT = 'MMM dd, yyyy';
export const TIME_FORMAT = 'h:mm a';
export const DATETIME_FORMAT = 'MMM dd, yyyy h:mm a';

export const DEFAULT_MAP_CENTER = {
  lat: 40.7128,
  lng: -74.0060
};

export const MAP_ZOOM_LEVELS = {
  CITY: 11,
  NEIGHBORHOOD: 13,
  STREET: 15,
  BUILDING: 18
};

export const NOTIFICATION_TYPES = {
  SESSION_REQUEST: 'session-request',
  SESSION_ACCEPTED: 'session-accepted',
  SESSION_REJECTED: 'session-rejected',
  SESSION_UPDATE: 'session-update',
  SESSION_CANCELLED: 'session-cancelled',
  SESSION_COMPLETED: 'session-completed',
  PAYMENT_RECEIVED: 'payment-received',
  APPLICATION_UPDATE: 'application-update'
};

export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection.',
  SERVER_ERROR: 'Server error. Please try again later.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  SESSION_EXPIRED: 'Your session has expired. Please login again.',
  NOT_FOUND: 'The requested resource was not found.'
};

export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: 'Login successful!',
  REGISTRATION_SUCCESS: 'Registration successful!',
  PROFILE_UPDATE: 'Profile updated successfully!',
  SESSION_BOOKED: 'Session booked successfully!',
  SESSION_CANCELLED: 'Session cancelled successfully!',
  APPLICATION_SUBMITTED: 'Application submitted successfully!',
  PAYMENT_SUCCESS: 'Payment processed successfully!'
};

export const VALIDATION_RULES = {
  EMAIL: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
  PHONE: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
  ZIP_CODE: /^\d{5}(-\d{4})?$/,
  PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/
};

export const MAP_CONFIG = {
  DEFAULT_CENTER: {
    lat: 25.7617,
    lng: -80.1918 // Miami, FL
  },
  DEFAULT_ZOOM: 13,
  SEARCH_RADIUS_KM: 50
};